import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Input, Select, DatePicker, Button } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { getReportAuditTrail } from '../../services/regulatoryReportingService';

const { Option } = Select;
const { RangePicker } = DatePicker;

const ACTION_TYPES = [
  'GENERATED',
  'MODIFIED',
  'APPROVED',
  'REJECTED',
  'EXPORTED',
  'SUBMITTED',
  'SCHEDULED',
  'TEMPLATE_CREATED',
  'TEMPLATE_UPDATED',
];

const ACTION_COLORS = {
  GENERATED: 'blue',
  MODIFIED: 'orange',
  APPROVED: 'green',
  REJECTED: 'red',
  EXPORTED: 'purple',
  SUBMITTED: 'cyan',
  SCHEDULED: 'geekblue',
  TEMPLATE_CREATED: 'lime',
  TEMPLATE_UPDATED: 'gold',
};

const DEFAULT_FILTERS = {
  search: '',
  action: null,
  startDate: null,
  endDate: null,
};

const AuditTrailTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchLogs = useCallback(async (page = 1, size = 10, f = appliedFilters) => {
    setLoading(true);
    try {
      const res = await getReportAuditTrail({ page: page - 1, size, ...f });
      if (res?.status) {
        setLogs(res.data?.content || []);
        setPagination({ current: page, pageSize: size, total: res.data?.totalElements || 0 });
      }
    } catch (err) {
      console.error('Failed to fetch audit trail:', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    fetchLogs(1, pagination.pageSize, filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    fetchLogs(1, pagination.pageSize, DEFAULT_FILTERS);
  };

  const handleChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilters({
        ...filters,
        startDate: dates[0]?.format('YYYY-MM-DD'),
        endDate: dates[1]?.format('YYYY-MM-DD'),
      });
    } else {
      setFilters({ ...filters, startDate: null, endDate: null });
    }
  };

  const columns = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (action) => (
        <Tag
          color={ACTION_COLORS[action] || 'default'}
          style={{ fontWeight: 600, borderRadius: 6, fontSize: 11 }}
        >
          {action}
        </Tag>
      ),
    },
    {
      title: 'Report Name',
      dataIndex: 'reportName',
      key: 'reportName',
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{name || '—'}</span>,
    },
    {
      title: 'Performed By',
      dataIndex: 'performedBy',
      key: 'performedBy',
      render: (name) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
          <UserOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
          {name || '—'}
        </span>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
      render: (detail) => <span style={{ color: '#595959', fontSize: 12 }}>{detail || '—'}</span>,
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
      render: (ip) => (
        <span style={{ fontFamily: 'monospace', color: '#595959', fontSize: 12 }}>
          {ip || '—'}
        </span>
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (ts) => (
        <span style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
          <ClockCircleOutlined />
          {ts ? moment(ts).format('MMM DD, YYYY • hh:mm A') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* ── Filter Bar ──────────────────────────────── */}
      <div className="reg-filter-bar">
        <div className="reg-filter-row">
          <div className="reg-filter-item filter-search">
            <span className="reg-filter-label">Search</span>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Report name, user..."
              allowClear
              value={filters.search}
              onChange={(e) => handleChange('search', e.target.value)}
            />
          </div>
          <div className="reg-filter-item">
            <span className="reg-filter-label">Action Type</span>
            <Select
              placeholder="All Actions"
              allowClear
              value={filters.action || undefined}
              onChange={(val) => handleChange('action', val)}
            >
              {ACTION_TYPES.map((a) => (
                <Option key={a} value={a}>{a.replace(/_/g, ' ')}</Option>
              ))}
            </Select>
          </div>
          <div className="reg-filter-item">
            <span className="reg-filter-label">Date Range</span>
            <RangePicker onChange={handleDateChange} format="YYYY-MM-DD" style={{ width: '100%' }} />
          </div>
          <div className="reg-filter-actions">
            <Button icon={<FilterOutlined />} onClick={handleApplyFilters} className="reg-filter-apply-btn">
              Filter
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleResetFilters} className="reg-filter-reset-btn">
              Reset
            </Button>
          </div>
        </div>
      </div>

      {/* ── Table ──────────────────────────────── */}
      <div className="reg-table-card">
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} entries`,
          }}
          onChange={handleTableChange}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'reg-row-even' : 'reg-row-odd')}
          className="reg-table"
        />
      </div>
    </div>
  );
};

export default AuditTrailTab;
